/**
 * Mariner Spirit — GA4 event tracking for critical conversion actions.
 * Requires gtag (ms-gtag.liquid) and pubsub.js for cart events.
 *
 * Purchase events: checkout runs on checkout.shopify.com — add the custom pixel in
 * assets/ms-shopify-ga4-custom-pixel.js via Settings → Customer events.
 * Order page tracking below is a backup only.
 */
(function () {
  function track(eventName, params) {
    if (typeof window.gtag !== 'function') return;
    window.gtag('event', eventName, params || {});
  }

  function centsToMajor(value) {
    const n = Number(value);
    if (!Number.isFinite(n)) return undefined;
    return n / 100;
  }

  function lineItemFromPayload(payload) {
    const cartData = payload?.cartData;
    const variantId = payload?.productVariantId;

    if (cartData?.items?.length) {
      return cartData.items[cartData.items.length - 1];
    }
    if (cartData?.variant_id || cartData?.id) {
      return cartData;
    }
    if (variantId) {
      return { variant_id: variantId };
    }
    return null;
  }

  function trackPurchase(payload, source) {
    if (!payload || !payload.transaction_id) return;

    const storageKey = `ms_ga_purchase_${payload.transaction_id}`;
    try {
      if (window.localStorage.getItem(storageKey) === '1') return;
    } catch (e) {
      /* ignore storage errors */
    }

    track('purchase', {
      transaction_id: payload.transaction_id,
      value: payload.value,
      currency: payload.currency,
      tax: payload.tax,
      shipping: payload.shipping,
      items: payload.items,
      purchase_source: source || 'unknown',
    });

    try {
      window.localStorage.setItem(storageKey, '1');
    } catch (e) {
      /* ignore storage errors */
    }
  }

  function initCheckoutFormTracking() {
    document.addEventListener(
      'submit',
      (event) => {
        const form = event.target;
        if (!(form instanceof HTMLFormElement)) return;

        const action = form.getAttribute('action') || '';
        if (action.includes('/checkout') || form.querySelector('[name="checkout"]')) {
          track('begin_checkout', { checkout_method: 'form_submit', form_action: action || undefined });
        }
      },
      true
    );
  }

  function initPurchaseFromOrderPage() {
    const dataEl = document.getElementById('MsGaPurchaseData');
    if (!dataEl) return;

    try {
      const payload = JSON.parse(dataEl.textContent || '');
      trackPurchase(payload, 'order_page');
    } catch (e) {
      /* invalid JSON */
    }
  }

  function trackAddToCart(payload) {
    const item = lineItemFromPayload(payload);
    if (!item) return;

    const value = centsToMajor(item.final_line_price ?? item.line_price ?? item.price);
    const itemId = String(item.variant_id || item.id || payload?.productVariantId || '');
    const itemName = item.product_title || item.title || item.name || undefined;

    track('add_to_cart', {
      currency: item.currency || window.Shopify?.currency?.active,
      value,
      items: [
        {
          item_id: itemId,
          item_name: itemName,
          item_category: payload?.source || 'unknown',
          price: centsToMajor(item.price),
          quantity: item.quantity || 1,
        },
      ],
      cart_source: payload?.source,
    });
  }

  function trackAddToCartError(payload) {
    track('add_to_cart_error', {
      cart_source: payload?.source,
      item_id: payload?.productVariantId ? String(payload.productVariantId) : undefined,
      error_message:
        typeof payload?.message === 'string'
          ? payload.message
          : typeof payload?.errors === 'string'
            ? payload.errors
            : undefined,
    });
  }

  function trackCrewLead(payload) {
    track('generate_lead', {
      lead_type: 'crew_application',
      application_type: payload?.application,
      specialty: payload?.specialty || undefined,
      team_preference: payload?.team_preference || undefined,
      founders_circle: payload?.founders_circle || undefined,
      marketing_opt_in: payload?.accepts_marketing ? 'yes' : 'no',
    });
  }

  function initPubSub() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

    subscribe(PUB_SUB_EVENTS.cartUpdate, (payload) => {
      if (!payload?.cartData || payload.cartData.status) return;
      trackAddToCart(payload);
    });

    subscribe(PUB_SUB_EVENTS.cartError, (payload) => {
      trackAddToCartError(payload);
    });
  }

  function initClickTracking() {
    document.addEventListener(
      'click',
      (event) => {
        const target = event.target instanceof Element ? event.target : null;
        if (!target) return;

        const paywallLink = target.closest('.ms-crew-form__paywall-actions a');
        if (paywallLink) {
          const href = paywallLink.getAttribute('href') || '';
          if (/crew-application|application.fee|application-fee/i.test(href)) {
            track('crew_fee_checkout_start', { link_url: href });
          } else if (/account\/login|\/login/i.test(href)) {
            track('login', { method: 'crew_paywall', link_url: href });
          } else if (/join-crew|employment/i.test(href)) {
            track('crew_fee_continue', { link_url: href });
          }
          return;
        }

        const checkoutLink = target.closest('a[href*="/checkout"]');
        if (checkoutLink) {
          track('begin_checkout', { link_url: checkoutLink.href, checkout_method: 'link' });
          return;
        }

        const checkoutButton = target.closest('.cart__checkout-button, button[name="checkout"]');
        if (checkoutButton) {
          track('begin_checkout', { checkout_method: 'button' });
          return;
        }

        const foundersLink = target.closest(
          'a[href*="founders-circle"], a[href*="/pages/founders"]'
        );
        if (foundersLink && !foundersLink.closest('.ms-crew-form__paywall')) {
          track('select_promotion', {
            promotion_name: 'founders_circle',
            link_url: foundersLink.href,
          });
        }
      },
      true
    );
  }

  function initNewsletterTracking() {
    document.querySelectorAll('.newsletter-form__message--success:not([data-ms-analytics-tracked])').forEach((el) => {
      el.setAttribute('data-ms-analytics-tracked', 'true');
      track('sign_up', { method: 'shopify_customer', signup_location: signupLocation(el) });
    });

    document.addEventListener(
      'submit',
      (event) => {
        const form = event.target;
        if (!(form instanceof HTMLFormElement)) return;

        if (form.classList.contains('ms-mailchimp-form')) {
          track('sign_up', { method: 'mailchimp', signup_location: signupLocation(form) });
          return;
        }

        if (form.classList.contains('newsletter-form')) {
          track('sign_up', { method: 'shopify_customer', signup_location: signupLocation(form) });
        }
      },
      true
    );
  }

  function signupLocation(el) {
    const section = el.closest('.shopify-section');
    if (section?.id) return section.id.replace('shopify-section-', '');
    if (el.closest('.footer-block--newsletter')) return 'footer';
    if (el.closest('.ms-founder-cta')) return 'founder_cta';
    return 'unknown';
  }

  function klaviyoSignupLocation(detail) {
    const formId = detail?.formId ? String(detail.formId) : '';
    if (formId) {
      const embed = document.querySelector(`[class*="klaviyo-form-${formId}"]`);
      if (embed) return signupLocation(embed);
    }
    const anyEmbed = document.querySelector('[data-ms-klaviyo-form]');
    if (anyEmbed) return signupLocation(anyEmbed);
    const source = detail?.metaData?.$source;
    if (typeof source === 'string' && source.trim()) return source.trim();
    return formId || 'unknown';
  }

  let klaviyoListenerBound = false;

  function initKlaviyoTracking() {
    if (klaviyoListenerBound) return;
    klaviyoListenerBound = true;

    // https://developers.klaviyo.com/en/docs/track_klaviyo_form_activity_using_javascript
    window.addEventListener('klaviyoForms', (event) => {
      const detail = event?.detail;
      if (!detail?.type) return;

      if (detail.type === 'submit') {
        track('sign_up', {
          method: 'klaviyo',
          klaviyo_form_id: detail.formId ? String(detail.formId) : undefined,
          signup_location: klaviyoSignupLocation(detail),
        });
        return;
      }

      if (detail.type === 'open' || detail.type === 'embedOpen') {
        track('form_open', {
          form_provider: 'klaviyo',
          klaviyo_form_id: detail.formId ? String(detail.formId) : undefined,
        });
      }
    });
  }

  initKlaviyoTracking();

  function initPageContext() {
    if (document.querySelector('.ms-crew-application')) {
      track('view_form', { form_name: 'crew_application' });

      const success = document.querySelector('[data-ms-crew-success]:not([hidden])');
      if (success && !success.dataset.msAnalyticsTracked) {
        success.dataset.msAnalyticsTracked = 'true';
        track('generate_lead', { lead_type: 'crew_application', submission_method: 'page_load_success' });
      }
    }

    if (window.location.pathname.includes('founders-circle')) {
      track('view_promotion', { promotion_name: 'founders_circle' });
    }

    if (window.location.pathname.includes('join-crew') || window.location.pathname.includes('employment')) {
      track('view_form', { form_name: 'crew_application_landing' });
    }
  }

  function init() {
    initPubSub();
    initClickTracking();
    initCheckoutFormTracking();
    initNewsletterTracking();
    initPageContext();
    initPurchaseFromOrderPage();
  }

  window.msAnalytics = {
    track,
    trackPurchase,
    trackCrewLead,
    trackCrewError(message) {
      track('crew_application_error', { error_message: message });
    },
    trackCrewAccessGranted() {
      track('crew_access_granted', { access_type: 'fee_or_founder' });
    },
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', initNewsletterTracking);
})();
